/**
 * MenuBar Component
 *
 * Simple menu bar for web app with save/load functionality
 */

import { CSSProperties, useCallback, memo, useState, useEffect, useRef } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useThemeStore } from '@/stores/themeStore';
import { useViewStore } from '@/stores/viewStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { themes, ThemeName, BeatColorScheme, beatColorSchemes, applyBeatColorScheme } from '@/styles/themes';
import { AspectRatio } from '@/types';
import { downloadProject, uploadProject } from '@/utils/projectFileIO';
import { imageStore } from '@/utils/browserAPI';
import { blobManager } from '@/utils/blobManager';
import {
  exportToFountain,
  exportToPlainText,
  exportToEDL,
  exportToCSV,
  exportEDLWithImages,
  exportImageSequence,
} from '@/utils/exportUtils';
import { downloadText, downloadZip, downloadFile } from '@/utils/downloadHelper';
import { exportToPDF, exportScriptToPDF, type PDFLayoutStyle } from '@/utils/pdfExport';
import { saveProjectToDB } from '@/utils/indexedDB';
import LoadingModal from '@/components/LoadingModal/LoadingModal';
import KeyboardShortcuts from '@/components/KeyboardShortcuts/KeyboardShortcuts';
import ChangelogModal from '@/components/ChangelogModal/ChangelogModal';
import { APP_NAME, APP_VERSION, APP_DESCRIPTION } from '@/constants/app';

const AUTO_SAVE_PROJECT_ID = 'autosave';

function MenuBar() {
  const nodes = useProjectStore((state) => state.nodes);
  const titleNodes = useProjectStore((state) => state.titleNodes);
  const commentBoxes = useProjectStore((state) => state.commentBoxes);
  const projectName = useProjectStore((state) => state.projectName);
  const projectAuthor = useProjectStore((state) => state.projectAuthor);
  const projectDescription = useProjectStore((state) => state.projectDescription);
  const fps = useProjectStore((state) => state.fps);
  const aspectRatio = useProjectStore((state) => state.aspectRatio);
  const beatColorScheme = useProjectStore((state) => state.beatColorScheme);
  const setProjectName = useProjectStore((state) => state.setProjectName);
  const setFps = useProjectStore((state) => state.setFps);
  const setAspectRatio = useProjectStore((state) => state.setAspectRatio);
  const setBeatColorScheme = useProjectStore((state) => state.setBeatColorScheme);
  const loadProject = useProjectStore((state) => state.loadProject);
  const getProjectData = useProjectStore((state) => state.getProjectData);

  const showNotification = useNotificationStore((state) => state.showNotification);

  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  const autoSwitchViewAfterSend = useViewStore((state) => state.autoSwitchViewAfterSend);
  const setAutoSwitchViewAfterSend = useViewStore((state) => state.setAutoSwitchViewAfterSend);

  // Settings dropdown state
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Save dropdown state
  const [showSave, setShowSave] = useState(false);
  const saveRef = useRef<HTMLDivElement>(null);

  // Export dropdown state
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // PDF layout submenu state
  const [showPDFSubmenu, setShowPDFSubmenu] = useState(false);

  // Help dropdown state
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // Loading modal state
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  // Keyboard shortcuts modal state
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Changelog modal state
  const [showChangelog, setShowChangelog] = useState(false);

  // About modal state
  const [showAbout, setShowAbout] = useState(false);

  const handleDownload = useCallback(async () => {
    try {
      const referenceNodes = useProjectStore.getState().referenceNodes;
      const noteNodes = useProjectStore.getState().noteNodes;
      const titleNodes = useProjectStore.getState().titleNodes;
      const audioTracks = useProjectStore.getState().audioTracks;
      const author = useProjectStore.getState().projectAuthor;
      const description = useProjectStore.getState().projectDescription;
      await downloadProject(projectName, fps, aspectRatio, nodes, referenceNodes, noteNodes, titleNodes, audioTracks, commentBoxes, author, description);
      console.log('Project downloaded successfully');
    } catch (error) {
      console.error('Failed to download project:', error);
      alert('Failed to download project. Please try again.');
    }
  }, [projectName, fps, aspectRatio, nodes, commentBoxes]);

  /**
   * Manual save to browser storage (IndexedDB)
   * Triggers an immediate save without waiting for auto-save delay
   */
  const handleSaveToBrowser = useCallback(async () => {
    try {
      if (projectName === 'Untitled Project') {
        showNotification('Please name your project first', 'warning', 3000);
        return;
      }

      const projectData = getProjectData();
      await saveProjectToDB(AUTO_SAVE_PROJECT_ID, projectName, projectData, imageStore);
      showNotification('Project saved to browser', 'info', 2000);
      console.log('[MenuBar] Manual save to browser completed');
    } catch (error) {
      console.error('Failed to save to browser:', error);
      showNotification('Failed to save project to browser', 'error', 3000);
    }
  }, [projectName, getProjectData, showNotification]);

  const handleUpload = useCallback(async () => {
    try {
      const result = await uploadProject();
      if (!result) return; // User cancelled

      // Clear existing stores
      // Note: uploadProject already created blob URLs and registered them in blobManager
      // We just need to clear OLD images and add legacy beat images to imageStore
      imageStore.clear();
      console.log('[MenuBar] Loading', result.images.size, 'images');

      // Add legacy beat images to imageStore
      // Reference/node images are already in blobManager from uploadProject
      for (const [key, blobUrl] of result.images.entries()) {
        if (!key.startsWith('reference-image:') && !key.startsWith('node-image:')) {
          // Legacy beat images - add to imageStore
          imageStore.set(key, blobUrl);
          console.log('[MenuBar] Added to imageStore:', key);
        } else {
          console.log('[MenuBar] Reference/node image already in blobManager:', key);
        }
      }

      console.log('[MenuBar] imageStore size:', imageStore.size, 'blobManager count:', blobManager.getCount());

      // Load project into store
      loadProject(
        {
          version: '1.0',
          name: result.projectName,
          fps: result.fps,
          aspectRatio: result.aspectRatio || '16:9',
          nodes: result.nodes,
          referenceNodes: result.referenceNodes || [],
          noteNodes: result.noteNodes || [],
          titleNodes: result.titleNodes || [],
          audioTracks: result.audioTracks || [],
          commentBoxes: result.commentBoxes || [],
          sceneHeadingHistory: result.sceneHeadingHistory || [],
          author: result.author,
          description: result.description,
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        },
        '' // Empty path for web app
      );

      console.log('Project loaded successfully');
    } catch (error) {
      console.error('Failed to load project:', error);
      alert('Failed to load project. Please try again.');
    }
  }, [loadProject]);

  const handleNewProject = useCallback(() => {
    if (nodes.length > 0) {
      const confirmed = confirm('Create a new project? Any unsaved changes will be lost.');
      if (!confirmed) return;
    }

    // Clear everything
    imageStore.clear();
    loadProject(
      {
        version: '1.0',
        name: 'Untitled Project',
        fps: 24,
        aspectRatio: '16:9',
        nodes: [],
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      },
      '' // Empty path for web app
    );
  }, [nodes, loadProject]);

  const handleRename = useCallback(() => {
    const newName = prompt('Enter project name:', projectName);
    if (newName && newName.trim()) {
      setProjectName(newName.trim());
    }
  }, [projectName, setProjectName]);

  const handleFpsChange = useCallback((newFps: number) => {
    setFps(newFps);
    setShowSettings(false);
  }, [setFps]);

  const handleAspectRatioChange = useCallback((newAspectRatio: AspectRatio) => {
    setAspectRatio(newAspectRatio);
    setShowSettings(false);
  }, [setAspectRatio]);

  const handleThemeChange = useCallback((newTheme: ThemeName) => {
    setTheme(newTheme);
    // Reapply beat color scheme after theme change to preserve user's selection
    applyBeatColorScheme(beatColorScheme);
    setShowSettings(false);
  }, [setTheme, beatColorScheme]);

  const handleBeatColorSchemeChange = useCallback((newScheme: BeatColorScheme) => {
    setBeatColorScheme(newScheme);
    setShowSettings(false);
  }, [setBeatColorScheme]);

  // ---------------------------------------------------------------------------
  // EXPORT HANDLERS
  // ---------------------------------------------------------------------------

  const handleExportFountain = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting Fountain script...');
      const result = exportToFountain({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.content) {
        downloadText(result.content, `${projectName}.fountain`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export Fountain script');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export Fountain script. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes]);

  const handleExportPlainText = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting plain text script...');
      const result = exportToPlainText({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.content) {
        downloadText(result.content, `${projectName}_script.txt`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export plain text script');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export plain text script. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes]);

  const handleExportScriptPDF = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting script PDF...');
      const blob = await exportScriptToPDF({ projectName, nodes, author: projectAuthor, description: projectDescription });
      downloadFile(blob, `${projectName}_script.pdf`, 'application/pdf');
      setShowExport(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export script PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, nodes, projectAuthor, projectDescription]);

  const handleExportEDL = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting EDL...');
      const result = exportToEDL({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.content) {
        downloadText(result.content, `${projectName}.edl`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export EDL');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export EDL. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes]);

  const handleExportCSV = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting CSV...');
      const result = exportToCSV({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.content) {
        downloadText(result.content, `${projectName}_beats.csv`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export CSV');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes]);

  const handleExportImages = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting image sequence...');
      const result = await exportImageSequence({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.files) {
        // Create ZIP with images
        await downloadZip(result.files, `${projectName}_images.zip`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export images');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export images. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes]);

  const handleExportEDLWithImages = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportMessage('Exporting EDL package with images...');
      const result = await exportEDLWithImages({ projectName, fps, aspectRatio, nodes });
      if (result.success && result.files) {
        // Create ZIP with EDL and images
        await downloadZip(result.files, `${projectName}_edl_package.zip`);
        setShowExport(false);
      } else {
        alert(result.error || 'Failed to export EDL package');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export EDL package. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes, titleNodes]);

  const handleExportPDF = useCallback(async (layout: PDFLayoutStyle) => {
    try {
      setIsExporting(true);
      setExportMessage('Generating PDF storyboard...');
      const pdfBlob = await exportToPDF({
        projectName,
        fps,
        aspectRatio,
        nodes,
        titleNodes,
        layout,
        includeTimecodes: true,
        includeCoverPage: true,
      });

      const layoutName = layout === 'page-per-beat' ? 'detailed' :
                         layout === 'two-up' ? '2up' :
                         layout === 'four-up' ? '4up' : '6up';
      downloadFile(pdfBlob, `${projectName}_storyboard_${layoutName}.pdf`, 'application/pdf');
      setShowExport(false);
      setShowPDFSubmenu(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export PDF storyboard. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [projectName, fps, aspectRatio, nodes, titleNodes]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
      if (saveRef.current && !saveRef.current.contains(event.target as Node)) {
        setShowSave(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setShowExport(false);
        setShowPDFSubmenu(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setShowHelp(false);
      }
    };

    if (showSettings || showSave || showExport || showHelp) {
      // Use capture phase to ensure we catch events before React Flow
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }
  }, [showSettings, showSave, showExport, showHelp]);

  // Keyboard shortcuts listener
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Close modals with Escape
      if (event.key === 'Escape') {
        if (showKeyboardShortcuts) {
          event.preventDefault();
          setShowKeyboardShortcuts(false);
        }
        if (showChangelog) {
          event.preventDefault();
          setShowChangelog(false);
        }
        if (showAbout) {
          event.preventDefault();
          setShowAbout(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showKeyboardShortcuts, showChangelog, showAbout]);

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const menuBarStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-xs)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-tertiary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    fontSize: 'var(--nb-font-size-xs)',
    position: 'relative',
  };

  const buttonStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-primary)',
  };

  const projectNameStyles: CSSProperties = {
    marginLeft: 'var(--nb-space-sm)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    color: 'var(--nb-text-primary)',
    cursor: 'pointer',
    borderRadius: 'var(--nb-radius-sm)',
    fontSize: 'var(--nb-font-size-md)',
    fontWeight: 'bold',
  };

  const settingsButtonStyles: CSSProperties = {
    ...buttonStyles,
    fontSize: 'var(--nb-font-size-md)',
  };

  const settingsDropdownStyles: CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 'var(--nb-space-sm)',
    marginTop: 'var(--nb-space-xs)',
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    minWidth: 150,
  };

  const settingsItemStyles: CSSProperties = {
    padding: 'var(--nb-space-sm)',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    transition: 'background-color var(--nb-transition-fast)',
  };

  const settingsSectionHeaderStyles: CSSProperties = {
    padding: 'var(--nb-space-sm)',
    borderBottom: '1px solid var(--nb-border-primary)',
    fontWeight: 600,
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
  };

  const menuItemStyles: CSSProperties = {
    display: 'block',
    width: '100%',
    padding: 'var(--nb-space-sm) var(--nb-space-md)',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    transition: 'background-color var(--nb-transition-fast)',
  };

  return (
    <div style={menuBarStyles}>
      <button style={buttonStyles} onClick={handleNewProject}>
        New
      </button>
      <button style={buttonStyles} onClick={handleUpload}>
        Open...
      </button>

      {/* Save Dropdown */}
      <div ref={saveRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          style={buttonStyles}
          onClick={() => setShowSave(!showSave)}
          title="Save project"
        >
          Save ▾
        </button>

        {showSave && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'var(--nb-bg-secondary)',
            border: '1px solid var(--nb-border-primary)',
            borderRadius: 'var(--nb-radius-sm)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            minWidth: '180px',
            zIndex: 1000,
            marginTop: '2px',
          }}>
            <div
              style={settingsItemStyles}
              onClick={() => {
                handleSaveToBrowser();
                setShowSave(false);
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Save project to browser storage (IndexedDB) - auto-loads on next visit"
            >
              Save to Browser
            </div>
            <div
              style={{...settingsItemStyles, borderBottom: 'none'}}
              onClick={() => {
                handleDownload();
                setShowSave(false);
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Download project as .emberboard file"
            >
              Save to File...
            </div>
          </div>
        )}
      </div>

      {/* Export Button */}
      <div ref={exportRef} style={{ position: 'relative' }}>
        <button
          style={buttonStyles}
          onClick={() => setShowExport(!showExport)}
          title="Export project in various formats"
        >
          Export
        </button>

        {/* Export Dropdown */}
        {showExport && (
          <div style={settingsDropdownStyles}>
            <div style={settingsSectionHeaderStyles}>
              Storyboard
            </div>
            <div
              style={{...settingsItemStyles, position: 'relative'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)';
                setShowPDFSubmenu(true);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Export visual storyboard as PDF"
            >
              PDF Storyboard →

              {/* PDF Submenu */}
              {showPDFSubmenu && (
                <div
                  style={{
                    ...settingsDropdownStyles,
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    marginTop: 0,
                    marginLeft: 4,
                  }}
                  onMouseEnter={() => setShowPDFSubmenu(true)}
                  onMouseLeave={() => setShowPDFSubmenu(false)}
                >
                  <div
                    style={settingsItemStyles}
                    onClick={() => handleExportPDF('page-per-beat')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="One beat per page (detailed)"
                  >
                    Detailed (1 per page)
                  </div>
                  <div
                    style={settingsItemStyles}
                    onClick={() => handleExportPDF('two-up')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Two beats per page"
                  >
                    2-Up (2 per page)
                  </div>
                  <div
                    style={settingsItemStyles}
                    onClick={() => handleExportPDF('four-up')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Four beats per page (2x2 grid)"
                  >
                    4-Up (2x2 grid)
                  </div>
                  <div
                    style={{...settingsItemStyles, borderBottom: 'none'}}
                    onClick={() => handleExportPDF('six-up')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Six beats per page (2x3 grid)"
                  >
                    6-Up (2x3 grid)
                  </div>
                </div>
              )}
            </div>

            <div style={settingsSectionHeaderStyles}>
              Scripts
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportFountain}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export screenplay as Fountain format (.fountain)"
            >
              Fountain Script
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportPlainText}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export script as plain text (.txt)"
            >
              Plain Text Script
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportScriptPDF}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export script as PDF (no images)"
            >
              Script PDF
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportCSV}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export beat list as CSV spreadsheet"
            >
              CSV Beat List
            </div>

            <div style={settingsSectionHeaderStyles}>
              Editorial
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportEDL}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export Edit Decision List for NLE software (.edl)"
            >
              EDL (Edit List)
            </div>
            <div
              style={settingsItemStyles}
              onClick={handleExportEDLWithImages}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export EDL with images in a ZIP package"
            >
              EDL + Images (ZIP)
            </div>

            <div style={settingsSectionHeaderStyles}>
              Images
            </div>
            <div
              style={{...settingsItemStyles, borderBottom: 'none'}}
              onClick={handleExportImages}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Export sequential numbered images (ZIP)"
            >
              Image Sequence (ZIP)
            </div>
          </div>
        )}
      </div>

      {/* Settings Button */}
      <div ref={settingsRef} style={{ position: 'relative' }}>
        <button
          style={settingsButtonStyles}
          onClick={() => setShowSettings(!showSettings)}
          title="Project Settings"
        >
          Settings
        </button>

        {/* Settings Dropdown */}
        {showSettings && (
          <div style={settingsDropdownStyles}>
            {/* Theme Section */}
            <div style={settingsSectionHeaderStyles}>
              Theme
            </div>
            {Object.values(themes).map((themeOption) => (
              <div
                key={themeOption.name}
                style={settingsItemStyles}
                onClick={() => handleThemeChange(themeOption.name)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={`Switch to ${themeOption.displayName} theme`}
              >
                {theme === themeOption.name && '✓ '}{themeOption.displayName}
              </div>
            ))}

            {/* Frame Rate Section */}
            <div style={settingsSectionHeaderStyles}>
              Frame Rate
            </div>
            {[24, 25, 30].map((fpsOption) => (
              <div
                key={fpsOption}
                style={settingsItemStyles}
                onClick={() => handleFpsChange(fpsOption)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={`Set frame rate to ${fpsOption} fps`}
              >
                {fps === fpsOption && '✓ '}{fpsOption} fps
              </div>
            ))}

            {/* Aspect Ratio Section */}
            <div style={settingsSectionHeaderStyles}>
              Aspect Ratio
            </div>
            {(['16:9', '9:16', '1:1'] as AspectRatio[]).map((ratioOption) => (
              <div
                key={ratioOption}
                style={settingsItemStyles}
                onClick={() => handleAspectRatioChange(ratioOption)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={`Set aspect ratio to ${ratioOption} (${ratioOption === '16:9' ? 'Landscape' : ratioOption === '9:16' ? 'Portrait' : 'Square'})`}
              >
                {aspectRatio === ratioOption && '✓ '}{ratioOption} {ratioOption === '16:9' ? '(Landscape)' : ratioOption === '9:16' ? '(Portrait)' : '(Square)'}
              </div>
            ))}

            {/* Beat Color Scheme Section */}
            <div style={settingsSectionHeaderStyles}>
              Beat Color
            </div>
            {(Object.keys(beatColorSchemes) as BeatColorScheme[]).map((scheme) => (
              <div
                key={scheme}
                style={{...settingsItemStyles, borderBottom: '1px solid var(--nb-border-primary)'}}
                onClick={() => handleBeatColorSchemeChange(scheme)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={`Timeline and selection colors: ${scheme === 'blue-green' ? 'Blue/Green' : 'Orange/Grey'}`}
              >
                {beatColorScheme === scheme && '✓ '}{scheme === 'blue-green' ? 'Blue/Green' : 'Orange/Grey'}
              </div>
            ))}

            {/* Auto-switch to Beats Section */}
            <div style={settingsSectionHeaderStyles}>
              References
            </div>
            <div
              style={settingsItemStyles}
              onClick={() => setAutoSwitchViewAfterSend(!autoSwitchViewAfterSend)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Automatically switch to Beats view after sending reference as beat"
            >
              {autoSwitchViewAfterSend && '✓ '}Auto-switch to Beats
            </div>
          </div>
        )}
      </div>

      {/* Help Dropdown */}
      <div ref={helpRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          style={buttonStyles}
          onClick={() => setShowHelp(!showHelp)}
          title="Help and Documentation"
        >
          Help ▾
        </button>
        
        {showHelp && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'var(--nb-bg-secondary)',
            border: '1px solid var(--nb-border-primary)',
            borderRadius: 'var(--nb-radius-sm)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            minWidth: '200px',
            zIndex: 1000,
            marginTop: '2px',
          }}>
            
            <button
              style={{
                ...menuItemStyles,
                borderBottom: '1px solid var(--nb-border-primary)',
              }}
              onClick={() => {
                setShowKeyboardShortcuts(true);
                setShowHelp(false);
              }}
            >
              Keyboard Shortcuts
            </button>
            
            <button
              style={menuItemStyles}
              onClick={() => {
                window.open('https://emberboard.com/docs/getting-started', '_blank');
                setShowHelp(false);
              }}
            >
              Getting Started
            </button>
            
            <button
              style={menuItemStyles}
              onClick={() => {
                window.open('https://emberboard.com/docs/shortcuts', '_blank');
                setShowHelp(false);
              }}
            >
              User Guide
            </button>
            
            <button
              style={menuItemStyles}
              onClick={() => {
                window.open('https://emberboard.com', '_blank');
                setShowHelp(false);
              }}
            >
              View Documentation
            </button>
            
            <button
              style={menuItemStyles}
              onClick={() => {
                setShowChangelog(true);
                setShowHelp(false);
              }}
            >
              What's New
            </button>

            <button
              style={{
                ...menuItemStyles,
                borderTop: '1px solid var(--nb-border-primary)',
              }}
              onClick={() => {
                window.open('https://emberboard.com/contact', '_blank');
                setShowHelp(false);
              }}
            >
              Contact & Support
            </button>
          </div>
        )}
      </div>

      {/* About Button */}
      <button
        style={buttonStyles}
        onClick={() => setShowAbout(true)}
        title={`About ${APP_NAME}`}
      >
        About
      </button>

      {/* Ko-fi Support Button */}
      <button
        style={buttonStyles}
        onClick={() => window.open('https://ko-fi.com/gnomelight', '_blank')}
        title="Support development ☕"
      >
        ☕ Support
      </button>

      {/* Project Name */}
      <div
        style={projectNameStyles}
        onClick={handleRename}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--nb-bg-secondary)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Click to rename"
      >
        {projectName}
      </div>

      {/* App Title with Logo */}
      <div style={{
        marginLeft: 'auto',
        padding: 'var(--nb-space-xs) var(--nb-space-sm)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--nb-space-xs)',
        fontSize: 'var(--nb-font-size-sm)',
        color: 'var(--nb-text-primary)',
        userSelect: 'none',
      }}>
        <img 
          src="./emberboard_horizontal_logo.svg" 
          alt="EmberBoard"
          style={{
            height: '20px',
            width: 'auto',
          }}
        />
        <span style={{ fontWeight: 'bold' }}>{APP_VERSION}</span>
      </div>

      {/* Loading Modal */}
      <LoadingModal message={exportMessage} isVisible={isExporting} />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcuts
        isVisible={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Changelog Modal */}
      <ChangelogModal
        isVisible={showChangelog}
        onClose={() => setShowChangelog(false)}
      />

      {/* About Modal */}
      {showAbout && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowAbout(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--nb-bg-secondary)',
              border: '1px solid var(--nb-border-primary)',
              borderRadius: 'var(--nb-radius-md)',
              padding: 'var(--nb-space-lg)',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              margin: '0 0 var(--nb-space-md) 0',
              color: 'var(--nb-text-primary)',
              fontSize: 'var(--nb-font-size-xl)',
            }}>
              About {APP_NAME}
            </h2>
            
            <p style={{
              color: 'var(--nb-text-secondary)',
              fontSize: 'var(--nb-font-size-sm)',
              margin: '0 0 var(--nb-space-md) 0',
              lineHeight: 1.6,
            }}>
              <strong>Version:</strong> {APP_VERSION}
            </p>

            <p style={{
              color: 'var(--nb-text-secondary)',
              fontSize: 'var(--nb-font-size-sm)',
              margin: '0 0 var(--nb-space-md) 0',
              lineHeight: 1.6,
            }}>
              {APP_DESCRIPTION}
            </p>

            <button
              style={{
                ...buttonStyles,
                width: '100%',
                marginBottom: 'var(--nb-space-lg)',
              }}
              onClick={() => window.open('https://emberboard.com', '_blank')}
            >
              📖 View Documentation
            </button>

            <div style={{
              borderTop: '1px solid var(--nb-border-primary)',
              paddingTop: 'var(--nb-space-md)',
              marginBottom: 'var(--nb-space-md)',
            }}>
              <p style={{
                color: 'var(--nb-text-secondary)',
                fontSize: 'var(--nb-font-size-sm)',
                margin: '0 0 var(--nb-space-sm) 0',
              }}>
                Love {APP_NAME}? Support development:
              </p>
              <button
                style={{
                  ...buttonStyles,
                  backgroundColor: 'var(--nb-accent-primary)',
                  color: 'white',
                  fontWeight: 500,
                  width: '100%',
                }}
                onClick={() => window.open('https://ko-fi.com/gnomelight', '_blank')}
              >
                ☕ Support on Ko-fi
              </button>
            </div>

            <button
              style={{
                ...buttonStyles,
                width: '100%',
              }}
              onClick={() => setShowAbout(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(MenuBar);
